import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Upload, Trash2, ArrowLeft, CalendarClock, Lock } from "lucide-react";
import WhatsAppButton from "../components/WhatsAppButton";
import ScheduleBadge from "../components/ScheduleBadge";
import MondayCountdown from "../components/MondayCountdown";
import PairChat from "../components/PairChat";

function Screenshot({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let created = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        created = URL.createObjectURL(resp.data);
        if (alive) setUrl(created);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!url) return <div className="font-mono text-[10px] text-[#71717A]">[ LOADING SCREENSHOT… ]</div>;
  return <img src={url} alt="match screenshot" className="w-full border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid="leg-screenshot" />;
}

function LegCard({ match, me, game1, onChanged }) {
  const isHome = me?.id === match.home_id;
  const isAway = me?.id === match.away_id;
  const mySubmission = isHome ? match.home_submission : isAway ? match.away_submission : null;
  const otherSubmission = isHome ? match.away_submission : isAway ? match.home_submission : null;
  const finalized = match.status === "confirmed" || match.admin_override;
  // Game 2 is LOCKED until Game 1 is finalized (backend enforces; this is UI-only)
  const isGame2 = match.order === 2;
  const game1Finalized = !game1 || game1.status === "confirmed" || game1.admin_override;
  const locked = isGame2 && !game1Finalized;
  const canSubmit = (isHome || isAway) && !mySubmission && !finalized && !locked;
  const canDelete = (isHome || isAway) && mySubmission && !finalized;

  // Perspective-based scores: what THIS user scored vs what their opponent scored
  const [myScore, setMyScore] = useState("");
  const [opponentScore, setOpponentScore] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showScreenshots, setShowScreenshots] = useState(false);
  const fileRef = useRef();

  const opponent = isHome ? match.away : match.away ? match.home : null;
  const myUsername = isHome ? match.home?.username : match.away?.username;
  const opponentUsername = isHome ? match.away?.username : match.home?.username;
  const myRoleLabel = isHome ? "HOME" : isAway ? "AWAY" : null;

  const formValid = myScore !== "" && opponentScore !== "" && !!file;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Fill in both scores AND attach a screenshot before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("my_score", String(myScore));
      form.append("opponent_score", String(opponentScore));
      form.append("screenshot", file);
      await api.post(`/matches/${match.id}/submit`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Result submitted.");
      setMyScore(""); setOpponentScore("");
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete your submission and submit again?")) return;
    try {
      await api.delete(`/matches/${match.id}/submission`);
      toast.success("Submission deleted. You can submit again.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusColor =
    match.status === "confirmed" ? "text-[#34C759] border-[#34C759]" :
    match.status === "disputed" ? "text-[#E11D48] border-[#E11D48]" :
    "text-[#7C3AED] border-[#7C3AED]";

  return (
    <article className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 ${locked ? "border-t-[#E11D48] opacity-75" : "border-t-[#7C3AED]"} p-6`} data-testid={`leg-${match.id}`}>
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-heading text-base uppercase tracking-[0.2em] px-2 py-0.5 ${match.order === 1 ? "bg-[#7C3AED] text-white" : locked ? "bg-[#E11D48]/80 text-[#09090B] dark:text-[#F4F4F5]" : "bg-[#EA580C] text-white"}`} data-testid={`game-label-${match.id}`}>
            GAME {match.order || (match.round > 19 ? 2 : 1)}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
            {match.order === 1 ? "HOME LEG" : "REVERSE LEG"} · RD {match.round}{match.scheduled_at ? <span className="text-[#7C3AED]"> · {new Date(match.scheduled_at + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase()}</span> : null}
          </div>
          <ScheduleBadge match={match} />
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.3em] border px-2 py-1 ${statusColor}`}>{match.status}{match.admin_override ? " · ADMIN" : ""}{match.auto_drawn ? " · AUTO" : ""}</span>
      </header>

      {myRoleLabel && (
        <div className="mb-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] border border-[#7C3AED]/40 px-3 py-1" data-testid={`role-tag-${match.id}`}>
          You play as {myRoleLabel} this leg
        </div>
      )}

      <div className="grid grid-cols-3 items-center gap-2 text-center">
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">HOME</div>
          <div className={`font-heading text-xl uppercase ${match.home_id === me?.id ? "text-[#34C759]" : ""}`}>{match.home?.username}</div>
        </div>
        <div className="font-mono text-3xl text-[#09090B] dark:text-[#F4F4F5]">
          {finalized ? `${match.confirmed_score_home}  :  ${match.confirmed_score_away}` : "—  :  —"}
        </div>
        <div className="text-left">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">AWAY</div>
          <div className={`font-heading text-xl uppercase ${match.away_id === me?.id ? "text-[#34C759]" : ""}`}>{match.away?.username}</div>
        </div>
      </div>

      {/* Submissions row */}
      <div className="grid grid-cols-2 gap-2 mt-5 text-sm">
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Home reported</div>
          <div className="font-mono text-base">{match.home_submission ? `${match.home_submission.score_home} : ${match.home_submission.score_away}` : "—"}</div>
        </div>
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Away reported</div>
          <div className="font-mono text-base">{match.away_submission ? `${match.away_submission.score_home} : ${match.away_submission.score_away}` : "—"}</div>
        </div>
      </div>

      {(match.home_submission || match.away_submission) && (
        <button
          type="button"
          onClick={() => setShowScreenshots((s) => !s)}
          data-testid={`toggle-screenshots-${match.id}`}
          className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] hover:text-[#09090B]"
        >
          {showScreenshots ? "// hide screenshots" : "// view screenshots"}
        </button>
      )}
      {showScreenshots && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {match.home_submission?.screenshot_path && <Screenshot path={match.home_submission.screenshot_path} />}
          {match.away_submission?.screenshot_path && <Screenshot path={match.away_submission.screenshot_path} />}
        </div>
      )}

      {locked && (
        <div className="mt-6 pt-6 border-t border-[#E4E4E7] dark:border-[#2A2A30] bg-[#E11D48]/5 border border-[#E11D48]/40 p-5" data-testid={`game2-locked-${match.id}`}>
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#E11D48] mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-heading text-base uppercase tracking-widest text-[#E11D48]">Game 2 is locked</div>
              <div className="font-mono text-xs text-[#71717A] mt-2 tracking-[0.1em] leading-relaxed">
                Play Game 1 first. Both players must submit matching scores + screenshots for Game 1. Once Game 1 is confirmed,
                Game 2 unlocks automatically — no admin action needed.
              </div>
            </div>
          </div>
        </div>
      )}

      {canSubmit && (
        <form onSubmit={onSubmit} className="mt-6 pt-6 border-t border-[#E4E4E7] dark:border-[#2A2A30] space-y-4" data-testid={`submit-form-${match.id}`}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// submit your result</div>
            <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.2em] mt-1">
              ★ Enter scores from YOUR perspective — never get home/away wrong again.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-2">
                Your score
              </label>
              <div className="font-mono text-[10px] text-[#71717A] mb-1 truncate">({myUsername})</div>
              <input
                data-testid={`my-score-${match.id}`}
                type="number" min={0} max={99} required
                value={myScore} onChange={(e) => setMyScore(e.target.value)}
                placeholder="0"
                className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border-2 border-[#7C3AED]/60 px-3 py-2 text-[#09090B] dark:text-[#F4F4F5] font-mono text-2xl focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">
                Opponent score
              </label>
              <div className="font-mono text-[10px] text-[#71717A] mb-1 truncate">({opponentUsername})</div>
              <input
                data-testid={`opponent-score-${match.id}`}
                type="number" min={0} max={99} required
                value={opponentScore} onChange={(e) => setOpponentScore(e.target.value)}
                placeholder="0"
                className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-[#09090B] dark:text-[#F4F4F5] font-mono text-2xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED]"
              />
            </div>
          </div>

          <label className="block border-2 border-dashed border-[#E4E4E7] dark:border-[#2A2A30] p-5 text-center cursor-pointer hover:border-[#7C3AED] transition-colors" data-testid={`dropzone-${match.id}`}>
            <input ref={fileRef} data-testid={`file-${match.id}`} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" required />
            <Upload className="w-6 h-6 mx-auto text-[#7C3AED] mb-2" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
              {file ? `> ${file.name}` : "[ UPLOAD FINAL-SCORE SCREENSHOT (REQUIRED) ]"}
            </div>
          </label>

          <button
            data-testid={`submit-btn-${match.id}`}
            type="submit"
            disabled={submitting || !formValid}
            title={!formValid ? "Fill both scores AND attach a screenshot" : ""}
            className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-6 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "SUBMITTING…" : "Submit this leg"}
          </button>
          {!formValid && (
            <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.2em]" data-testid={`form-help-${match.id}`}>
              [ COMPLETE BOTH SCORES AND ATTACH A SCREENSHOT TO SUBMIT ]
            </div>
          )}
        </form>
      )}

      {(isHome || isAway) && mySubmission && !finalized && (
        <div className="mt-6 pt-6 border-t border-[#E4E4E7] dark:border-[#2A2A30] flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-[0.25em]">
            {otherSubmission
              ? match.status === "disputed"
                ? "SCORES DISAGREE — either side can delete & resubmit to fix."
                : "OPPONENT HAS SUBMITTED — waiting for confirmation."
              : "WAITING FOR OPPONENT TO SUBMIT…"}
          </div>
          {canDelete && (
            <button onClick={onDelete} data-testid={`delete-submission-${match.id}`} className="border border-[#E11D48]/60 text-[#E11D48] px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-[#E11D48] hover:text-[#09090B] transition-colors inline-flex items-center gap-2">
              <Trash2 className="w-3 h-3" /> Edit / resubmit
            </button>
          )}
        </div>
      )}

      {/* Reschedule controls */}
      {(isHome || isAway) && !finalized && (
        <RescheduleControls match={match} me={me} onChanged={onChanged} />
      )}
    </article>
  );
}

function RescheduleControls({ match, me, onChanged }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(match.scheduled_at || "");
  const [busy, setBusy] = useState(false);
  const proposals = match.reschedule_proposals || {};
  const otherId = me?.id === match.home_id ? match.away_id : match.home_id;
  const myProposal = proposals[me?.id];
  const otherProposal = proposals[otherId];

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const minDate = today.toISOString().slice(0, 10);

  const propose = async () => {
    if (!date) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date });
      if (data.applied) toast.success(`Rescheduled to ${new Date(date + "T00:00:00").toLocaleDateString()}`);
      else toast(`Proposal sent. Waiting for opponent to confirm ${new Date(date + "T00:00:00").toLocaleDateString()}.`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.delete(`/matches/${match.id}/reschedule`);
      toast.success("Proposal withdrawn.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptOpponent = async () => {
    setDate(otherProposal);
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date: otherProposal });
      if (data.applied) toast.success(`Rescheduled to ${new Date(otherProposal + "T00:00:00").toLocaleDateString()}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`reschedule-${match.id}`}>
      {otherProposal && otherProposal !== myProposal && (
        <div className="mb-3 bg-[#7C3AED]/5 border border-[#7C3AED]/40 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`opp-proposal-${match.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED]">
            Opponent proposed {new Date(otherProposal + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
          </div>
          <button
            data-testid={`accept-proposal-${match.id}`}
            onClick={acceptOpponent} disabled={busy}
            className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-3 py-1 text-xs hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
          >
            Accept
          </button>
        </div>
      )}

      {myProposal && otherProposal !== myProposal && (
        <div className="mb-3 font-mono text-[10px] text-[#71717A] tracking-[0.2em]" data-testid={`my-proposal-${match.id}`}>
          You proposed {new Date(myProposal + "T00:00:00").toLocaleDateString()} — waiting on opponent.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
          Scheduled {match.scheduled_at ? new Date(match.scheduled_at + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "—"}
        </div>
        <div className="flex gap-2">
          {myProposal && (
            <button onClick={withdraw} disabled={busy} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1 text-[10px] uppercase tracking-widest hover:border-[#E11D48] hover:text-[#E11D48] transition-colors disabled:opacity-50" data-testid={`withdraw-${match.id}`}>
              Withdraw
            </button>
          )}
          <button onClick={() => setOpen((o) => !o)} className="border border-[#7C3AED]/40 text-[#7C3AED] px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-[#7C3AED] hover:text-white transition-colors inline-flex items-center gap-1" data-testid={`reschedule-btn-${match.id}`}>
            <CalendarClock className="w-3 h-3" /> Reschedule
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2 items-center" data-testid={`reschedule-form-${match.id}`}>
          <input
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1.5 text-[#09090B] dark:text-[#F4F4F5] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED]"
            data-testid={`reschedule-date-${match.id}`}
          />
          <button
            onClick={propose}
            disabled={busy || !date}
            className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-3 py-1.5 text-xs hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
            data-testid={`reschedule-submit-${match.id}`}
          >
            {busy ? "…" : "Propose"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function H2H() {
  const { opponentId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const prevStatusRef = useRef({});

  const refresh = async (silent = false) => {
    try {
      const { data: fresh } = await api.get(`/h2h/${opponentId}`);

      // Detect status transitions on each leg → toast
      const prev = prevStatusRef.current;
      const next = {};
      for (const m of fresh.matches || []) {
        next[m.id] = m.status;
        const before = prev[m.id];
        if (before && before !== m.status) {
          if (m.status === "confirmed") {
            const winId = m.confirmed_score_home > m.confirmed_score_away
              ? m.home_id
              : m.confirmed_score_away > m.confirmed_score_home
                ? m.away_id
                : null;
            const youWon = winId && winId === user?.id;
            const opponentWon = winId && winId !== user?.id;
            if (youWon) {
              toast.success(`✓ Round ${m.round} confirmed — you win ${m.confirmed_score_home}-${m.confirmed_score_away}! +3 pts`);
            } else if (opponentWon) {
              toast(`Round ${m.round} confirmed — final ${m.confirmed_score_home}-${m.confirmed_score_away}.`);
            } else {
              toast(`Round ${m.round} confirmed — draw ${m.confirmed_score_home}-${m.confirmed_score_away}. +1 pt`);
            }
          } else if (m.status === "disputed") {
            toast.error(`Round ${m.round} disputed — your score didn't match your opponent's. Admin will review.`);
          }
        }
      }
      prevStatusRef.current = next;
      setData(fresh);
    } catch (e) {
      if (!silent) setErr(formatApiError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
    // Poll for opponent submissions every 5s while on this page
    const iv = setInterval(() => refresh(true), 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [opponentId]);

  if (loading) return <TactLoader />;
  if (err) return <Panel className="font-mono text-[#E11D48] text-sm" testId="h2h-error">[ {err} ]</Panel>;
  if (!data) return null;

  return (
    <div data-testid="h2h-page" className="space-y-6">
      <button onClick={() => nav(-1)} className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] inline-flex items-center gap-2" data-testid="h2h-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="h2h-header">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// head to head · {data.league?.name}</div>
          <div className="font-heading text-3xl sm:text-4xl uppercase tracking-wider mt-1">
            {data.me?.username} <span className="text-[#71717A] text-base font-mono">VS</span> {data.opponent?.username}
          </div>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">
            One <span className="text-[#7C3AED]">game</span> = <span className="text-[#09090B] dark:text-[#F4F4F5]">2 legs</span> (home + reverse). Both legs are scheduled the same day. Play <span className="text-[#7C3AED]">Game 1</span> first — once both players confirm Game 1, <span className="text-[#EA580C]">Game 2</span> unlocks.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <MondayCountdown compact />
          {data.opponent?.whatsapp && (
            <WhatsAppButton number={data.opponent.whatsapp} testId="h2h-whatsapp" size="sm" label={`WhatsApp ${data.opponent.username}`} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="h2h-legs">
        {[...(data.matches || [])].sort((a, b) => (a.order || 1) - (b.order || 1)).map((m, _idx, sorted) => {
          const game1 = sorted.find((x) => (x.order || 1) === 1);
          return <LegCard key={m.id} match={m} me={user} game1={game1} onChanged={refresh} />;
        })}
      </div>

      {data.matches && data.matches.length > 0 && (
        <PairChat
          matchId={data.matches[0].id}
          opponentName={data.opponent?.username || "opponent"}
        />
      )}
    </div>
  );
}
